import React from 'react';
import './DailyUsageTable.css';

interface DailyUsageData {
  year: number;
  month: number;
  daysInMonth: number;
  dailyTotals: { [key: number]: number };
  totalClients: number;
  monthlyTotal: number;
}

interface DailyUsageTableProps {
  data: DailyUsageData | null;
  isLoading: boolean;
}

const DailyUsageTable: React.FC<DailyUsageTableProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="daily-usage-loading">
        <div className="loading-spinner"></div>
        <p>Cargando datos de uso diario...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="daily-usage-empty">
        <p>No hay datos disponibles para mostrar</p>
      </div>
    );
  }

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const getIntensityClass = (usage: number, maxUsage: number) => {
    if (usage === 0) return 'intensity-0';
    const percentage = (usage / maxUsage) * 100;
    if (percentage <= 10) return 'intensity-1';
    if (percentage <= 25) return 'intensity-2';
    if (percentage <= 50) return 'intensity-3';
    if (percentage <= 75) return 'intensity-4';
    return 'intensity-5';
  };

  const maxDailyUsage = Math.max(...Object.values(data.dailyTotals));

  return (
    <div className="daily-usage-container">
      <div className="daily-usage-header">
        <h2>Uso Diario Total - {monthNames[data.month - 1]} {data.year}</h2>
        <div className="daily-usage-stats">
          <div className="stat-item">
            <span className="stat-label">Total del Mes:</span>
            <span className="stat-value">{data.monthlyTotal.toLocaleString()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Clientes Activos:</span>
            <span className="stat-value">{data.totalClients}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Promedio Diario:</span>
            <span className="stat-value">
              {Math.round(data.monthlyTotal / data.daysInMonth).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Día Máximo:</span>
            <span className="stat-value">{maxDailyUsage.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="daily-usage-table-container">
        <table className="daily-usage-table">
          <thead>
            <tr>
              <th className="month-header">Mes</th>
              {Array.from({ length: data.daysInMonth }, (_, i) => i + 1).map(day => (
                <th key={day} className="day-header">
                  {day}
                </th>
              ))}
              <th className="total-header">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="month-cell">
                {monthNames[data.month - 1]} {data.year}
              </td>
              {Array.from({ length: data.daysInMonth }, (_, i) => i + 1).map(day => {
                const usage = data.dailyTotals[day] || 0;
                const intensityClass = getIntensityClass(usage, maxDailyUsage);
                
                return (
                  <td 
                    key={day} 
                    className={`usage-cell ${intensityClass}`}
                    title={`Día ${day}: ${usage.toLocaleString()} facturas`}
                  >
                    {usage > 0 ? usage.toLocaleString() : ''}
                  </td>
                );
              })}
              <td className="total-cell">
                {data.monthlyTotal.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="daily-usage-legend">
        <h4>Intensidad de Uso:</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color intensity-0"></div>
            <span>Sin uso</span>
          </div>
          <div className="legend-item">
            <div className="legend-color intensity-1"></div>
            <span>Muy bajo (1-10%)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color intensity-2"></div>
            <span>Bajo (11-25%)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color intensity-3"></div>
            <span>Medio (26-50%)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color intensity-4"></div>
            <span>Alto (51-75%)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color intensity-5"></div>
            <span>Muy alto (76-100%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyUsageTable;
